import { Editor } from '@tiptap/core';
import { CustomGroupSectionType } from 'api/custom-section/types/group-section';
import { isContentEditable } from 'components/organisms/document-editor/extensions/custom-section/utils/section-utils';

export const RL_GROUP_SECTION_NAME = 'RL_GROUP_SECTION';

export interface NodePosition {
  node: any;
  pos: number;
}

export const findNodeById = (
  editor: Editor,
  id: string,
  idType: 'templateSectionId' | 'documentSectionId',
): NodePosition | undefined => {
  let result: NodePosition | undefined;

  editor.state.doc.descendants((node, pos) => {
    if (
      (node.attrs[idType]?.toString() ?? node?.attrs?.tempId?.toString()) === id
    ) {
      if (isContentEditable(node)) {
        result = { node, pos };
      }

      return false;
    }

    return true;
  });

  return result;
};

export const findNodeByTemplateSectionId = (
  editor: Editor,
  templateSectionId: string,
): NodePosition | undefined => {
  return findNodeById(editor, templateSectionId, 'templateSectionId');
};

export const findNodeByDocumentId = (
  editor: Editor,
  documentId: string,
): NodePosition | undefined => {
  return findNodeById(editor, documentId, 'documentSectionId');
};

export const getNodePosition = (
  editor: Editor,
  identifierType: 'templateSectionId' | 'documentSectionId',
  identifierValue: string,
): NodePosition | undefined => {
  return identifierType === 'documentSectionId'
    ? findNodeByDocumentId(editor, identifierValue)
    : findNodeByTemplateSectionId(editor, identifierValue);
};

/** Call sites historically passed only the DOM element; the unused `editor` param meant scrolling never ran. */
export const scrollToLastNode = (targetNode?: Element | null): void => {
  setTimeout(() => {
    if (targetNode) {
      targetNode.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      });
    }
  }, 100);
};

/**
 * Resolves where `streamContent` should write, or `null` when this node should not stream
 * (missing node, group-only section, or multi-questions heading-only path).
 */
export const getStreamContentInsertPosition = (
  editor: Editor,
  nodePos: NodePosition,
  resolve: boolean,
): number | null => {
  const isMultiQuestionsGroup =
    nodePos.node.type?.name === RL_GROUP_SECTION_NAME &&
    nodePos.node.attrs?.sectionType === CustomGroupSectionType.MultiQuestions;

  if (isMultiQuestionsGroup && !resolve) {
    const groupTitle =
      nodePos.node.attrs?.title ??
      (nodePos.node.attrs?.config as Record<string, unknown>)?.title;

    if (groupTitle) {
      const headingNode =
        editor.schema.nodes.heading?.create(
          { level: 2 },
          editor.schema.text(String(groupTitle)),
        ) ??
        editor.schema.nodeFromJSON({
          type: 'heading',
          attrs: { level: 2 },
          content: [{ type: 'text', text: String(groupTitle) }],
        });

      editor.commands.command(({ tr, dispatch }) => {
        tr.insert(nodePos.pos + 1, headingNode);
        if (dispatch) {
          dispatch(tr);
        }

        return true;
      });
    }

    return null;
  }

  if (nodePos.node.type?.name === RL_GROUP_SECTION_NAME) {
    return null;
  }

  const parentNode =
    nodePos.pos > 0 ? editor.state.doc.resolve(nodePos.pos).parent : null;
  const additionalPos =
    parentNode?.type?.name === RL_GROUP_SECTION_NAME ? 1 : 3;

  return nodePos.pos + additionalPos;
};
